import React, { useState } from "react";
import { Alert, Button, Checkbox, Collapse, Radio, Space, Typography, Upload, notification } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { ScoringMode, submitJob } from "./api";
import { getEmailFromSessionStorage } from "../utils/session_utils";

const { Dragger } = Upload;
const { Paragraph, Text } = Typography;

interface Props {
  onSubmitted: () => void;
}

/** Upload an Amagi airing report and start the scoring job, or rescore from what is already loaded. */
const UploadTab: React.FC<Props> = ({ onSubmitted }) => {
  const [mode, setMode] = useState<ScoringMode>("load_and_score");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [timezoneUniversal, setTimezoneUniversal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const file = fileList[0]?.originFileObj as File | undefined;
  const canSubmit = mode === "rescore" || (Boolean(file) && timezoneUniversal);

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await submitJob(mode, mode === "load_and_score" ? file ?? null : null, getEmailFromSessionStorage() || "", timezoneUniversal);
      notification.success({
        message: "Scoring job submitted",
        description: `Job ${result.batchJobId.slice(0, 8)} is queued. Watch it on the Jobs tab; you will also get an email when it finishes.`,
      });
      setFileList([]);
      setTimezoneUniversal(false);
      onSubmitted();
    } catch (error: any) {
      notification.error({ message: "Could not submit the job", description: error?.response?.data?.message || error?.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 820 }}>
      <Paragraph>
        Upload the Amagi <Text strong>Content airing performance</Text> report. The job loads the new rows,
        rebuilds the slot baselines and episode scores for every platform, and writes the results to the
        scoring tables. Scores appear on the Scores tab when the job finishes.
      </Paragraph>

      <Collapse
        size="small"
        style={{ marginBottom: 16 }}
        items={[
          {
            key: "spec",
            label: "How to export the report (Amagi Analytics → Report generation)",
            children: (
              <>
                <Paragraph>
                  <Text strong>Timezone:</Text> Universal. The US/Eastern option is a fixed offset with no daylight
                  saving and is wrong for half the year; the job converts UTC to each platform&apos;s local time itself.
                </Paragraph>
                <Paragraph>
                  <Text strong>Dimensions, exactly these seven:</Text> Asset information, Channel, Platform, Air date,
                  Start time, Duration, Series.
                  <br />
                  <Text type="danger">Do not add</Text> Studio, Device brand, Device type, Country, Division or City:
                  they split one airing into several rows and the upload is refused.
                </Paragraph>
                <Paragraph>
                  <Text strong>Metrics:</Text> select all. Required: Total viewership mins. Used when present: Agg.
                  unique viewers, Number of sessions, Avg session duration, Ad impressions, the three Ad duration
                  fields and Ad server ad fill rate.
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text strong>Date range:</Text> the month to load, or longer. Overlap with earlier uploads is fine.
                </Paragraph>
              </>
            ),
          },
        ]}
      />

      <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ marginBottom: 16 }}>
        <Space direction="vertical">
          <Radio value="load_and_score">Load this file and rescore everything (the normal monthly run)</Radio>
          <Radio value="rescore">Rescore without a new file (rerun on the rows already loaded)</Radio>
        </Space>
      </Radio.Group>

      {mode === "load_and_score" && (
        <Dragger
          accept=".csv"
          maxCount={1}
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: next }) => setFileList(next.slice(-1))}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drag the CSV here or click to select it</p>
          <p className="ant-upload-hint">One file per job. Large files are fine; a year is about 55 MB.</p>
        </Dragger>
      )}

      {mode === "load_and_score" && (
        <Checkbox checked={timezoneUniversal} onChange={(e) => setTimezoneUniversal(e.target.checked)} style={{ marginBottom: 16 }}>
          This file was exported with <Text strong>Timezone = Universal</Text>
        </Checkbox>
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Re-uploading is safe"
        description={
          <>
            Rows are matched on content, platform, channel, date and start time. A row that is already
            loaded with the same numbers is left alone; one whose numbers changed, for example a corrected
            re-export from Amagi, is updated; a row not seen before is added. So re-uploading a file,
            uploading overlapping weeks, or uploading a corrected month all do the right thing. The Jobs
            tab shows how many rows each upload added, updated and left unchanged.
          </>
        }
      />

      <Button type="primary" onClick={submit} loading={submitting} disabled={!canSubmit}>
        {mode === "load_and_score" ? "Upload and score" : "Rescore now"}
      </Button>
    </div>
  );
};

export default UploadTab;
